import {
  CategoryLimitProgress,
  CategoryLimitSummary,
  RawCategoryLimitProgress,
  RawCategoryLimitSummary,
} from "@/types/category-limit.type";
import { API } from "./base.service";

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Clamp a percentage into the 0-100 range a progress bar can render.
 *
 * The API reports `percent_used` uncapped (160 when the user is 60% over
 * budget), so the real number is shown as text while the bar itself is
 * clamped.
 */
export const clampPercent = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  if (value < 0) return 0;
  if (value > 100) return 100;
  return value;
};

/** Normalize one row of the current-month payload. */
export const normalizeCategoryLimitProgress = (
  raw: RawCategoryLimitProgress
): CategoryLimitProgress => ({
  id: raw.id,
  category: raw.category,
  categoryName: raw.category_name ?? "Unknown category",
  categoryColor: raw.category_color ?? "#94a3b8",
  limit: toNumber(raw.limit),
  spent: toNumber(raw.spent),
  remaining: toNumber(raw.remaining),
  percentUsed: toNumber(raw.percent_used),
  overLimit: Boolean(raw.over_limit),
});

/**
 * Normalize GET /category-limits/current-month/ into camelCase with safe
 * defaults, so the UI never has to guard against partial payloads.
 */
export const normalizeCategoryLimitSummary = (
  raw: RawCategoryLimitSummary | null | undefined
): CategoryLimitSummary => {
  const results = (raw?.results ?? []).map(normalizeCategoryLimitProgress);

  return {
    month: raw?.month ?? "",
    periodStart: raw?.period_start ?? "",
    periodEnd: raw?.period_end ?? "",
    // Trust the server count when present, otherwise derive it.
    count: typeof raw?.count === "number" ? raw.count : results.length,
    totals: {
      limit: toNumber(raw?.totals?.limit),
      spent: toNumber(raw?.totals?.spent),
      remaining: toNumber(raw?.totals?.remaining),
    },
    results,
  };
};

/**
 * Pull a readable message out of a DRF error response.
 *
 * The limits endpoints return 400 with field errors (e.g. a foreign category
 * or a negative amount) and 404 for another user's limit.
 */
export const extractLimitErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  const response = (error as any)?.response;

  if (response?.status === 404) {
    return "That limit no longer exists. Refresh and try again.";
  }

  const data = response?.data;
  if (!data) {
    return error instanceof Error ? error.message : fallback;
  }

  if (typeof data === "string") return data;

  if (Array.isArray(data)) {
    return data.join(" ") || fallback;
  }

  if (typeof data === "object") {
    // Prefer the specific fields this endpoint validates.
    for (const field of ["amount", "category", "detail", "non_field_errors"]) {
      const value = (data as Record<string, unknown>)[field];
      if (typeof value === "string") return value;
      if (Array.isArray(value) && value.length) return String(value[0]);
    }

    const first = Object.values(data as Record<string, unknown>)[0];
    if (typeof first === "string") return first;
    if (Array.isArray(first) && first.length) return String(first[0]);
  }

  return fallback;
};

class _CategoryLimitAPI extends API {
  /**
   * Current-month spend vs limit for every category that has a limit.
   *
   * Pass a "YYYY-MM" month to view a specific past month; omit it for the
   * current month (the endpoint's default). The backend historizes spend but
   * not the limit amounts, so a past month shows this month's caps against
   * that month's spending.
   */
  getCurrentMonth = async (month?: string) =>
    this.request({
      url: "/current-month/",
      method: "GET",
      params: month ? { month } : undefined,
    });
}

export const CategoryLimitAPI = new _CategoryLimitAPI("category-limits");
